import type { AccountingDocumentRecord, ProjectAccountingDocument } from '@/lib/api/accounting';
import type { ExpenseEntry } from '@/lib/mock/expenses';

export type AccountingDocumentsFilterState = {
  search: string;
  dateFrom: string;
  dateTo: string;
  balanceFilter: 'all' | 'balanced' | 'unbalanced';
  projectQuery: string;
};

export const EMPTY_ACCOUNTING_DOCUMENTS_FILTER: AccountingDocumentsFilterState = {
  search: '',
  dateFrom: '',
  dateTo: '',
  balanceFilter: 'all',
  projectQuery: '',
};

/** نرمال‌سازی تاریخ شمسی برای مقایسه (فقط رقم) */
export function normalizeFaDateKey(value: string): string {
  const digits = (value || '').replace(/[^\d۰-۹]/g, (ch) => {
    const persian = '۰۱۲۳۴۵۶۷۸۹';
    const idx = persian.indexOf(ch);
    return idx >= 0 ? String(idx) : '';
  });
  return digits;
}

function matchesDateRange(
  dateFa: string,
  dateFrom: string,
  dateTo: string,
  normalizeDate: (v: string) => string,
): boolean {
  const from = dateFrom || dateTo;
  const to = dateTo || dateFrom;
  if (!from && !to) return true;
  const fromN = from ? normalizeDate(from) : '';
  const toN = to ? normalizeDate(to) : '';
  const d = normalizeDate(dateFa || '');
  if (!d) return false;
  if (fromN && toN) return d >= fromN && d <= toN;
  if (fromN) return d >= fromN;
  if (toN) return d <= toN;
  return true;
}

function matchesSearch(haystack: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return haystack.toLowerCase().includes(q);
}

export function filterAccountingDocuments(
  docs: AccountingDocumentRecord[],
  filters: AccountingDocumentsFilterState,
  normalizeDate: (v: string) => string = normalizeFaDateKey,
): AccountingDocumentRecord[] {
  const q = filters.search.trim();
  const projectQ = filters.projectQuery.trim();

  return docs.filter((doc) => {
    if (!matchesDateRange(doc.docDate, filters.dateFrom, filters.dateTo, normalizeDate)) {
      return false;
    }

    const diff = Number(doc.difference ?? 0);
    if (filters.balanceFilter === 'balanced' && diff !== 0) return false;
    if (filters.balanceFilter === 'unbalanced' && diff === 0) return false;

    const projectLabels = (doc.projectLabels || []).join(' ');
    if (projectQ && !matchesSearch(projectLabels, projectQ)) return false;

    if (q) {
      const blob = [doc.docNo, doc.docDate, doc.operationDescription, projectLabels].join(' ');
      if (!matchesSearch(blob, q)) return false;
    }

    return true;
  });
}

export type ProjectDocumentsFilterState = {
  search: string;
  dateFrom: string;
  dateTo: string;
  expenseTypeId: string;
  beneficiaryId: string;
};

export function filterProjectAccountingDocuments(
  docs: ProjectAccountingDocument[],
  filters: ProjectDocumentsFilterState,
  normalizeDate: (v: string) => string = normalizeFaDateKey,
): ProjectAccountingDocument[] {
  const q = filters.search.trim();

  return docs.filter((doc) => {
    if (!matchesDateRange(doc.docDate, filters.dateFrom, filters.dateTo, normalizeDate)) {
      return false;
    }

    const lines = doc.lines || [];
    if (filters.expenseTypeId) {
      const hit = lines.some((l) => l.expenseTypeId === filters.expenseTypeId);
      if (!hit) return false;
    }
    if (filters.beneficiaryId) {
      const hit = lines.some((l) => l.beneficiaryKey === filters.beneficiaryId);
      if (!hit) return false;
    }

    if (q) {
      const blob = [
        doc.docNo,
        doc.docDate,
        doc.operationDescription,
        ...lines.map((l) => `${l.beneficiary} ${l.accountTitle} ${l.description}`),
      ].join(' ');
      if (!matchesSearch(blob, q)) return false;
    }

    return true;
  });
}

export type GroupedProjectDocumentRow = {
  documentId: string;
  docNo: string;
  dateFa: string;
  operationDescription: string;
  beneficiaries: string;
  lineCount: number;
  totalDebit: number;
  totalCredit: number;
  difference: number;
};

function summarizeLinkedBeneficiaries(
  lines: Array<{ beneficiary?: string; debit?: number; credit?: number }>,
): string {
  const parts = new Map<string, { debit: number; credit: number }>();
  for (const line of lines) {
    const name = (line.beneficiary || '').trim();
    if (!name || name === '—') continue;
    const debit = Number(line.debit ?? 0);
    const credit = Number(line.credit ?? 0);
    const prev = parts.get(name) ?? { debit: 0, credit: 0 };
    prev.debit += debit;
    prev.credit += credit;
    parts.set(name, prev);
  }

  if (!parts.size) return '—';

  return [...parts.entries()]
    .map(([name, amounts]) => {
      if (amounts.debit > 0 && amounts.credit <= 0) return `${name} (بدهکار)`;
      if (amounts.credit > 0 && amounts.debit <= 0) return `${name} (بستانکار)`;
      return name;
    })
    .join(' · ');
}

/** تجمیع ردیف‌های هزینه به سطح سند (fallback وقتی API سند-محور در دسترس نیست) */
export function groupExpenseEntriesByDocument(entries: ExpenseEntry[]): GroupedProjectDocumentRow[] {
  const map = new Map<string, GroupedProjectDocumentRow>();

  for (const e of entries) {
    const documentId = e.documentId?.trim();
    if (!documentId) continue;

    const debit = Number(e.debit ?? 0);
    const credit = Number(e.credit ?? 0);
    const prev = map.get(documentId);
    if (!prev) {
      map.set(documentId, {
        documentId,
        docNo: e.docNo || '—',
        dateFa: e.dateFa || '—',
        operationDescription: e.description || '',
        beneficiaries: e.beneficiary && e.beneficiary !== '—' ? e.beneficiary : '—',
        lineCount: 1,
        totalDebit: debit,
        totalCredit: credit,
        difference: debit - credit,
      });
      continue;
    }
    prev.lineCount += 1;
    prev.totalDebit += debit;
    prev.totalCredit += credit;
    prev.difference = prev.totalDebit - prev.totalCredit;
    if (!prev.operationDescription && e.description) {
      prev.operationDescription = e.description;
    }
    if (e.beneficiary && e.beneficiary !== '—') {
      const parts = new Set(
        prev.beneficiaries === '—' ? [] : prev.beneficiaries.split(' · ').map((x) => x.replace(/ \(بدهکار\)| \(بستانکار\)$/, '')),
      );
      parts.add(e.beneficiary);
      prev.beneficiaries = [...parts].join(' · ');
    }
  }

  return [...map.values()].sort((a, b) => {
    const da = normalizeFaDateKey(a.dateFa);
    const db = normalizeFaDateKey(b.dateFa);
    return db.localeCompare(da) || String(b.docNo).localeCompare(String(a.docNo));
  });
}

export function projectAccountingDocumentToRow(doc: ProjectAccountingDocument): GroupedProjectDocumentRow {
  const lines = doc.lines || [];
  const totalDebit = lines.reduce((s, l) => s + Number(l.debit ?? 0), 0);
  const totalCredit = lines.reduce((s, l) => s + Number(l.credit ?? 0), 0);
  return {
    documentId: doc.id,
    docNo: doc.docNo,
    dateFa: doc.docDate,
    operationDescription: doc.operationDescription || '',
    beneficiaries: summarizeLinkedBeneficiaries(lines),
    lineCount: lines.length,
    totalDebit,
    totalCredit,
    difference: totalDebit - totalCredit,
  };
}
